/**
 * Filters — builds dynamic dropdown filters from sheet column values.
 * Adapted for MapLibre GL JS.
 */

/**
 * Build filter dropdowns and wire up show/hide logic.
 */
export function buildFilters(sidebarEl, locations, markers, map, config, clusterManager) {
  const filterSlot = sidebarEl.querySelector('.pm-filters-slot');
  if (!filterSlot) return;

  // Collapsible wrapper for mobile
  const filtersWrapper = document.createElement('div');
  filtersWrapper.className = 'pm-filters-wrapper';

  const toggleBtn = document.createElement('button');
  toggleBtn.className = 'pm-filters-toggle';
  toggleBtn.setAttribute('aria-expanded', 'false');
  toggleBtn.innerHTML = '<span class="pm-filters-toggle-icon">▸</span> Filters';
  toggleBtn.addEventListener('click', () => {
    const expanded = filtersWrapper.classList.toggle('pm-filters-open');
    toggleBtn.setAttribute('aria-expanded', expanded);
    toggleBtn.querySelector('.pm-filters-toggle-icon').textContent = expanded ? '▾' : '▸';
  });
  filtersWrapper.appendChild(toggleBtn);

  const filtersDiv = document.createElement('div');
  filtersDiv.className = 'pm-filters';
  filtersDiv.setAttribute('role', 'group');
  filtersDiv.setAttribute('aria-label', 'Filter locations');

  const filtersHeading = document.createElement('p');
  filtersHeading.className = 'pm-filters-heading';
  filtersHeading.setAttribute('role', 'button');
  filtersHeading.setAttribute('aria-expanded', 'true');
  filtersHeading.setAttribute('tabindex', '0');
  filtersHeading.innerHTML = 'Filters <span class="pm-filters-arrow">&#9660;</span>';
  filtersDiv.appendChild(filtersHeading);

  const filtersBody = document.createElement('div');
  filtersBody.className = 'pm-filters-body';
  filtersDiv.appendChild(filtersBody);

  filtersHeading.addEventListener('click', () => {
    const isCollapsed = filtersDiv.classList.toggle('pm-filters-collapsed');
    filtersHeading.setAttribute('aria-expanded', String(!isCollapsed));
  });
  filtersHeading.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      filtersHeading.click();
    }
  });

  const activeFilters = {};

  config.filters.forEach(filterName => {
    const key = filterName.toLowerCase().replace(/\s+/g, '_');

    const values = extractUniqueValues(locations, key);
    if (values.length === 0) {
      console.warn(`[PilotMaps] Filter column "${filterName}" not found or has no values.`);
      return;
    }

    activeFilters[key] = 'all';

    const group = document.createElement('div');
    group.className = 'pm-filter-group';

    const label = document.createElement('label');
    label.className = 'pm-filter-label';
    label.textContent = formatLabel(filterName);

    const selectId = 'pm-filter-' + key + '-' + Math.random().toString(36).substr(2, 4);
    label.setAttribute('for', selectId);
    group.appendChild(label);

    const select = document.createElement('select');
    select.className = 'pm-filter-select';
    select.id = selectId;
    select.setAttribute('aria-label', `Filter by ${formatLabel(filterName)}`);

    const allOption = document.createElement('option');
    allOption.value = 'all';
    allOption.textContent = `All ${formatLabel(filterName)}`;
    select.appendChild(allOption);

    values.sort().forEach(val => {
      const opt = document.createElement('option');
      opt.value = val;
      opt.textContent = val;
      select.appendChild(opt);
    });

    select.addEventListener('change', () => {
      activeFilters[key] = select.value;
      applyFilters(activeFilters, markers, map, sidebarEl, locations, clusterManager);
    });

    group.appendChild(select);
    filtersBody.appendChild(group);
  });

  filtersWrapper.appendChild(filtersDiv);
  filterSlot.appendChild(filtersWrapper);
}

/**
 * Extract unique values from a column across all locations.
 */
function extractUniqueValues(locations, key) {
  const values = new Set();
  locations.forEach(loc => {
    const val = loc[key] || loc._raw?.[key];
    if (!val) return;
    String(val).split(',').map(s => s.trim()).filter(Boolean).forEach(v => values.add(v));
  });
  return Array.from(values);
}

/**
 * Apply all active filters — show/hide markers and sidebar items.
 */
function applyFilters(activeFilters, markers, map, sidebarEl, locations, clusterManager) {
  const list = sidebarEl._pmList;
  const listItems = list ? list.querySelectorAll('.pm-location-item') : [];
  let visibleCount = 0;

  markers.forEach(({ marker, location }, i) => {
    const matches = matchesAllFilters(location, activeFilters);

    if (matches && !clusterManager) {
      if (marker) marker.addTo(map);
    } else if (!clusterManager) {
      if (marker) marker.remove();
    }

    if (listItems[i]) {
      listItems[i].setAttribute('data-filter-hidden', !matches);
      listItems[i].setAttribute('aria-hidden', !matches);
      listItems[i].style.display = matches ? '' : 'none';
    }

    if (matches) visibleCount++;
  });

  // No results message
  let noResults = sidebarEl.querySelector('.pm-no-results');
  if (visibleCount === 0) {
    if (!noResults) {
      noResults = document.createElement('div');
      noResults.className = 'pm-no-results';
      noResults.setAttribute('role', 'status');
      noResults.setAttribute('aria-live', 'polite');
      noResults.textContent = 'No locations match the selected filters.';
      if (list) list.parentNode.insertBefore(noResults, list.nextSibling);
    }
    noResults.style.display = '';
  } else if (noResults) {
    noResults.style.display = 'none';
  }

  // Update cluster index if clustering is enabled
  if (clusterManager) {
    const filteredLocations = [];
    markers.forEach(({ location }, i) => {
      const matches = matchesAllFilters(location, activeFilters);
      if (matches) filteredLocations.push(location);
    });
    clusterManager.updateLocations(filteredLocations);
  }

  // Screen reader announcement
  announceFilterResults(visibleCount, sidebarEl);
}

function matchesAllFilters(location, filters) {
  return Object.entries(filters).every(([key, value]) => {
    if (value === 'all') return true;
    const locVal = location[key] || location._raw?.[key];
    if (!locVal) return false;
    return String(locVal).split(',').map(s => s.trim().toLowerCase()).includes(value.toLowerCase());
  });
}

function announceFilterResults(count, container) {
  let announcer = container.querySelector('.pm-sr-announcer');
  if (!announcer) {
    announcer = document.createElement('div');
    announcer.className = 'pm-sr-announcer';
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);';
    container.appendChild(announcer);
  }
  announcer.textContent = count === 0
    ? 'No locations match the selected filters.'
    : `Showing ${count} location${count !== 1 ? 's' : ''}.`;
}

function formatLabel(str) {
  return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
